import React from 'react';
import { Button } from '../components/ui/button';
import { Factory, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const Industries = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-50 via-white to-orange-50 py-20 lg:py-32">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
              Industries We <span className="text-orange-600">Serve</span>
            </h1>
            <p className="text-lg lg:text-xl text-gray-600 leading-relaxed">
              Trusted by leading manufacturers across diverse sectors worldwide
            </p>
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-gradient-to-br from-white to-orange-50 rounded-xl p-8 shadow-lg hover:shadow-2xl transition-shadow border-2 border-orange-100">
              <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                <Factory className="h-10 w-10 text-orange-600" />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 text-center mb-4">Industrial</h3>
              <p className="text-gray-600 text-center mb-6">
                Heavy-duty tapes for manufacturing, assembly, packaging, and industrial processing applications requiring strength and durability.
              </p>
              <div className="space-y-2">
                <h4 className="font-semibold text-orange-600 mb-2">Applications:</h4>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li>• Manufacturing assembly lines</li>
                  <li>• Packaging and palletizing</li>
                  <li>• Heavy equipment maintenance</li>
                  <li>• Industrial sealing solutions</li>
                  <li>• Metal fabrication</li>
                </ul>
              </div>
            </div>

            <div className="bg-gradient-to-br from-white to-orange-50 rounded-xl p-8 shadow-lg hover:shadow-2xl transition-shadow border-2 border-orange-100">
              <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                <svg className="h-10 w-10 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 text-center mb-4">Automotive</h3>
              <p className="text-gray-600 text-center mb-6">
                Specialized tapes for wire harnessing, component mounting, interior assembly, and high-temperature automotive applications.
              </p>
              <div className="space-y-2">
                <h4 className="font-semibold text-orange-600 mb-2">Applications:</h4>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li>• Wire harness assembly</li>
                  <li>• Interior trim mounting</li>
                  <li>• Engine compartment sealing</li>
                  <li>• Sound dampening solutions</li>
                  <li>• Dashboard component assembly</li>
                </ul>
              </div>
            </div>

            <div className="bg-gradient-to-br from-white to-orange-50 rounded-xl p-8 shadow-lg hover:shadow-2xl transition-shadow border-2 border-orange-100">
              <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                <svg className="h-10 w-10 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
                </svg>
              </div>
              <h3 className="text-3xl font-bold text-gray-900 text-center mb-4">Electronics</h3>
              <p className="text-gray-600 text-center mb-6">
                Precision insulation tapes for circuit boards, transformers, motors, and sensitive electronic component protection and assembly.
              </p>
              <div className="space-y-2">
                <h4 className="font-semibold text-orange-600 mb-2">Applications:</h4>
                <ul className="space-y-1 text-sm text-gray-600">
                  <li>• PCB manufacturing</li>
                  <li>• Transformer insulation</li>
                  <li>• Motor winding protection</li>
                  <li>• Component masking</li>
                  <li>• EMI/RFI shielding</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Additional Industries */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow border border-gray-200">
              <h4 className="font-bold text-xl text-gray-900 mb-3">Construction</h4>
              <p className="text-sm text-gray-600">
                Weather sealing, HVAC applications, window installation, and insulation solutions.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow border border-gray-200">
              <h4 className="font-bold text-xl text-gray-900 mb-3">Medical Devices</h4>
              <p className="text-sm text-gray-600">
                Precision die-cuts, clean room applications, and medical device assembly.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow border border-gray-200">
              <h4 className="font-bold text-xl text-gray-900 mb-3">Aerospace</h4>
              <p className="text-sm text-gray-600">
                High-temperature tapes, Kapton film applications, and specialized bonding solutions.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow border border-gray-200">
              <h4 className="font-bold text-xl text-gray-900 mb-3">Renewable Energy</h4>
              <p className="text-sm text-gray-600">
                Solar panel assembly, wind turbine applications, and battery manufacturing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-orange-600 to-orange-700 text-white">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-5xl font-bold mb-6">Find the Right Solution for Your Industry</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Let our experts help you select the perfect adhesive tape for your specific application
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/products">
              <Button className="bg-white text-orange-600 hover:bg-gray-100 px-8 py-6 text-lg">
                View Products
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                Contact Expert
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Industries;